<div class="mp container-lg py-3">
  <h1 class="mp__title">{{ 'marketing_pool.title' | translate }}</h1>
  <p class="mp__intro">{{ 'marketing_pool.intro' | translate }}</p>

  @if (loading() && !overview()) {
    <div class="text-center py-4"><c-spinner /></div>
  } @else if (blocked(); as reason) {
    <c-card class="mp__card">
      <c-card-body>
        <p class="mb-3">{{ ('marketing_pool.blocked.' + reason) | translate }}</p>
        <button cButton color="primary" variant="outline" type="button" (click)="goDashboard()">
          {{ 'marketing_pool.back_to_dashboard' | translate }}
        </button>
      </c-card-body>
    </c-card>
  } @else if (overview(); as o) {

    <!-- ============================ Payment step (after the acceptance) ============================ -->
    @if (result() || cryptoOrderId()) {
      <c-card class="mp__card mp__card--accent">
        <c-card-body>
          @if (result(); as r) {
            <h2 class="mp__h">{{ 'marketing_pool.payment.title' | translate }}</h2>
            <p>{{ 'marketing_pool.payment.created' | translate:{ reference: r.investment.reference, amount: money(r.investment.amount_usd) } }}</p>

            @if (r.payment.method === 'bank_transfer') {
              <p>{{ 'marketing_pool.payment.bank_intro' | translate:{ amount: money(r.payment.amount) } }}</p>
              <dl class="mp__dl">
                <dt>{{ 'marketing_pool.payment.reference' | translate }}</dt>
                <dd><strong class="mp__ref">{{ r.payment.reference }}</strong></dd>
                <dt>{{ 'marketing_pool.payment.amount' | translate }}</dt>
                <dd>{{ money(r.payment.amount) }} ({{ r.payment.currency }})</dd>
                @for (row of bankRows(r.payment.bank); track row.key) {
                  @if (row.key !== 'reference') {
                    <dt>{{ ('marketing_pool.payment.bank.' + row.key) | translate }}</dt>
                    <dd>{{ row.value }}</dd>
                  }
                }
              </dl>
              @if (!r.payment.bank) {
                <div class="alert alert-warning">{{ 'marketing_pool.payment.bank_missing' | translate }}</div>
              }
              <p class="mp__hint">{{ 'marketing_pool.payment.bank_note' | translate }}</p>
              <button cButton color="primary" type="button" (click)="finish()">{{ 'marketing_pool.payment.done' | translate }}</button>
            }
          }

          @if (cryptoOrderId()) {
            <h2 class="mp__h">{{ 'marketing_pool.payment.crypto_title' | translate }}</h2>
            <p class="mp__hint">{{ 'marketing_pool.payment.crypto_intro' | translate:{ network: o.crypto_network } }}</p>
            @if (cryptoBusy()) {
              <div class="text-center py-3"><c-spinner /></div>
            } @else if (cryptoError(); as message) {
              <div class="alert alert-danger">{{ message }}</div>
              <button cButton color="primary" variant="outline" type="button"
                      (click)="startCrypto(cryptoOrderId()!, cryptoAmount())">{{ 'marketing_pool.retry' | translate }}</button>
              <button cButton color="secondary" variant="ghost" type="button" class="ms-2" (click)="finish()">
                {{ 'marketing_pool.payment.later' | translate }}
              </button>
            } @else if (cryptoRecipient()) {
              <!-- Never rendered without a recipient: ethers reads "" as an ENS name. -->
              <app-web3-confirmation
                [orderId]="cryptoOrderId()"
                [totalPrice]="cryptoAmount()"
                [recipientAddress]="cryptoRecipient()"
                (onSuccess)="onCryptoSuccess()"
                (onBack)="onCryptoBack()" />
              <button cButton color="secondary" variant="ghost" type="button" class="mt-2" (click)="finish()">
                {{ 'marketing_pool.payment.later' | translate }}
              </button>
            }
          }
        </c-card-body>
      </c-card>

    <!-- ============================ Acceptance view (one page) ============================ -->
    } @else if (accepting(); as inv) {
      <form class="mp__form" (ngSubmit)="submit()" novalidate>
        <button cButton color="secondary" variant="ghost" size="sm" type="button" class="mb-2" (click)="cancelAccept()">
          ‹ {{ 'marketing_pool.accept.back' | translate }}
        </button>
        <h2 class="mp__h">{{ 'marketing_pool.accept.title' | translate:{ reference: inv.reference } }}</h2>

        <!-- The backend's binding figures, before anything is ticked (customer correction 2026-10-28, point 4) -->
        <c-card class="mp__card mp__card--accent">
          <c-card-body>
            <h2 class="mp__h">{{ 'marketing_pool.accept.summary_title' | translate }}</h2>
            <dl class="mp__dl mb-0">
              <dt>{{ 'marketing_pool.contract.investment_id' | translate }}</dt><dd class="mp__ref">{{ inv.reference }}</dd>
              <dt>{{ 'marketing_pool.contract.amount' | translate }}</dt><dd><b>{{ money(inv.amount_usd) }}</b></dd>
              @if (inv.payout_factor) {
                <dt>{{ 'marketing_pool.contract.payout_factor' | translate }}</dt><dd>{{ factor(inv.payout_factor) }}@if (inv.payout_factor_source === 'personal') { <span class="mp__meta ms-1">({{ 'marketing_pool.contract.personal_factor' | translate }})</span> }</dd>
              }
              <dt>{{ 'marketing_pool.contract.total_payout' | translate }}</dt><dd><b>{{ money(inv.total_payout_usd) }}</b></dd>
            </dl>
            <p class="mp__hint mt-2 mb-0">{{ 'marketing_pool.offer.included' | translate }}</p>
          </c-card-body>
        </c-card>

        <!-- 1 · The contract with the partner's own data -->
        <c-card class="mp__card">
          <c-card-body>
            <h2 class="mp__h">1 · {{ 'marketing_pool.accept.contract_title' | translate }}</h2>
            @if (docsLoading()) {
              <div class="text-center py-3"><c-spinner /></div>
            } @else if (docsError(); as message) {
              <div class="alert alert-danger">{{ message }}</div>
              <button cButton color="primary" variant="outline" type="button" (click)="loadDocuments()">{{ 'marketing_pool.retry' | translate }}</button>
            } @else if (contractDoc(); as doc) {
              <section class="mp__doc">
                <div class="mp__doc-head">
                  <strong>{{ doc.title }}</strong>
                  <span class="mp__meta">{{ 'marketing_pool.accept.version' | translate:{ version: doc.version } }}</span>
                  @if (doc.draft) { <c-badge color="danger">{{ 'marketing_pool.draft_badge' | translate }}</c-badge> }
                </div>
                @if (doc.draft) {
                  <div class="mp__draft" role="note">{{ 'marketing_pool.draft_notice' | translate }}</div>
                }
                <div class="mp__doc-body" tabindex="0" [innerHTML]="doc.html ?? ''"></div>
                <div class="mp__accept">
                  <strong>{{ 'marketing_pool.accept.acceptance_title' | translate }}</strong>
                  <label class="mp__check">
                    <input type="checkbox" name="accept_contract" [(ngModel)]="contractAccepted" (ngModelChange)="onInput()" />
                    <span class="mp__pre">{{ (doc.checkbox || 'marketing_pool.checkbox.marketing_pool_contract') | translate }}</span>
                  </label>
                </div>
              </section>
            } @else {
              <div class="alert alert-warning mb-0">{{ 'marketing_pool.errors.documents_unavailable' | translate }}</div>
            }
          </c-card-body>
        </c-card>

        <!-- 2 · Declarations -->
        <c-card class="mp__card">
          <c-card-body>
            <h2 class="mp__h">2 · {{ 'marketing_pool.accept.declarations_title' | translate }}</h2>
            <fieldset class="mp__field">
              <legend>{{ 'marketing_pool.accept.us_citizen_question' | translate }}</legend>
              <div class="mp__radios">
                <label><input type="radio" name="us_citizen" value="no" [(ngModel)]="usCitizen" (ngModelChange)="onInput()" />
                  {{ 'marketing_pool.accept.us_citizen_no' | translate }}</label>
                <label><input type="radio" name="us_citizen" value="yes" [(ngModel)]="usCitizen" (ngModelChange)="onInput()" />
                  {{ 'marketing_pool.accept.us_citizen_yes' | translate }}</label>
              </div>
            </fieldset>
            <div class="mp__grid2">
              <label class="mp__field">
                <span>{{ 'marketing_pool.accept.citizenship' | translate }}</span>
                <select class="form-select" name="citizenship" [(ngModel)]="citizenship" (ngModelChange)="onInput()">
                  <option value="">{{ 'marketing_pool.accept.choose_country' | translate }}</option>
                  @for (c of countries; track c.code) { <option [value]="c.code">{{ c.name }}</option> }
                </select>
              </label>
              <label class="mp__field">
                <span>{{ 'marketing_pool.accept.residence' | translate }}</span>
                <select class="form-select" name="residence" [(ngModel)]="residence" (ngModelChange)="onInput()">
                  <option value="">{{ 'marketing_pool.accept.choose_country' | translate }}</option>
                  @for (c of countries; track c.code) { <option [value]="c.code">{{ c.name }}</option> }
                </select>
              </label>
            </div>
            @if (usCitizenExcluded()) {
              <div class="alert alert-danger mt-3 mb-0" role="alert">{{ 'marketing_pool.errors.us_citizen_excluded' | translate }}</div>
            }
            @if (usResidentExcluded()) {
              <div class="alert alert-danger mt-3 mb-0" role="alert">{{ 'marketing_pool.errors.us_resident_excluded' | translate }}</div>
            }
          </c-card-body>
        </c-card>

        <!-- 3 · The separate, voluntary declaration (waiver of the withdrawal right) -->
        <c-card class="mp__card">
          <c-card-body>
            <h2 class="mp__h">3 · {{ 'marketing_pool.waiver.title' | translate }}</h2>
            <div class="mp__doc mp__doc--optional">
              <p class="mp__hint">{{ 'marketing_pool.waiver.explain' | translate:{ days: o.withdrawal_days } }}</p>
              <label class="mp__check">
                <input type="checkbox" name="waiver" [(ngModel)]="waiver" (ngModelChange)="onInput()" />
                <span>{{ 'marketing_pool.waiver.text' | translate }}</span>
              </label>
            </div>
          </c-card-body>
        </c-card>

        <!-- 4 · Payment method -->
        <c-card class="mp__card">
          <c-card-body>
            <h2 class="mp__h">4 · {{ 'marketing_pool.accept.payment_title' | translate }}</h2>
            <div class="mp__methods">
              @for (m of o.payment_methods; track m) {
                <label class="mp__method" [class.is-active]="paymentMethod === m">
                  <input type="radio" name="payment_method" [value]="m" [(ngModel)]="paymentMethod" (ngModelChange)="onInput()" />
                  <span>
                    <strong>{{ ('marketing_pool.methods.' + m) | translate }}</strong>
                    <small>{{ ('marketing_pool.methods.' + m + '_hint') | translate:{ network: o.crypto_network } }}</small>
                  </span>
                </label>
              }
            </div>
            @if (paymentMethod === 'crypto') {
              <fieldset class="mp__field mt-3">
                <legend>{{ 'marketing_pool.accept.token' | translate }}</legend>
                <div class="mp__radios">
                  @for (t of o.crypto_tokens; track t) {
                    <label><input type="radio" name="crypto_token" [value]="t" [(ngModel)]="cryptoToken" (ngModelChange)="onInput()" />
                      {{ t }} ({{ o.crypto_network }})</label>
                  }
                </div>
                <p class="mp__hint mb-0">{{ 'marketing_pool.accept.token_hint' | translate }}</p>
              </fieldset>
            }

            @if (submitError(); as message) {
              <div class="alert alert-danger mt-3" role="alert">{{ message }}</div>
            }
            <button cButton color="primary" size="lg" type="submit" class="mt-3" [disabled]="!canSubmit()">
              {{ (submitting() ? 'marketing_pool.accept.submitting' : 'marketing_pool.accept.submit') | translate }}
            </button>
            @if (!canSubmit() && !submitting()) {
              <p class="mp__hint mt-2 mb-0">{{ 'marketing_pool.accept.submit_hint' | translate }}</p>
            }
          </c-card-body>
        </c-card>
      </form>

    } @else {

    <!-- Tabs -->
    <nav class="mp__tabs" role="tablist">
      <button type="button" role="tab" class="mp__tab" [class.is-active]="tab() === 'investments'"
              [attr.aria-selected]="tab() === 'investments'" (click)="selectTab('investments')">
        {{ 'marketing_pool.tabs.investments' | translate }}
        @if (investments().length) { <span class="mp__count">{{ investments().length }}</span> }
      </button>
      <button type="button" role="tab" class="mp__tab" [class.is-active]="tab() === 'info'"
              [attr.aria-selected]="tab() === 'info'" (click)="selectTab('info')">
        {{ 'marketing_pool.tabs.info' | translate }}
      </button>
      @if (showReferrals()) {
        <button type="button" role="tab" class="mp__tab" [class.is-active]="tab() === 'referrals'"
                [attr.aria-selected]="tab() === 'referrals'" (click)="selectTab('referrals')">
          {{ 'marketing_pool.tabs.referrals' | translate }} <span class="mp__count">{{ referrals().length }}</span>
        </button>
      }
    </nav>

    <!-- ============================ Information ============================ -->
    @if (tab() === 'info') {
      <c-card class="mp__card">
        <c-card-body>
          @if (docDraft()) {
            <div class="mp__draft" role="note">{{ 'marketing_pool.draft_notice' | translate }}</div>
          }
          <h2 class="mp__h">{{ 'marketing_pool.info.title' | translate }}</h2>
          <p>{{ 'marketing_pool.info.text' | translate }}</p>
          <p class="mp__hint">{{ 'marketing_pool.info.party' | translate }}</p>
          @if (!o.visible) {
            <div class="alert alert-info mb-0">{{ 'marketing_pool.notices.not_visible' | translate }}</div>
          } @else if (investBlock(); as block) {
            <div class="alert alert-info mb-0">{{ ('marketing_pool.notices.' + block) | translate }}</div>
          }
        </c-card-body>
      </c-card>
    }

    <!-- ============================ My participations ============================ -->
    @if (tab() === 'investments') {
      @if (factorNotice()) {
        <div class="alert alert-warning" role="alert">{{ 'marketing_pool.notices.factor_changed' | translate }}</div>
      }

      <!-- New participation: the partner's own amount (self-service, customer correction 2026-10-28) -->
      @if (o.visible) {
        <c-card class="mp__card">
          <c-card-body>
            <h2 class="mp__h">{{ 'marketing_pool.new.title' | translate }}</h2>
            <p class="mp__hint">{{ 'marketing_pool.new.intro' | translate:{ minimum: money(o.minimum_amount) } }}</p>
            @if (investBlock(); as block) {
              <div class="alert alert-info" role="note">{{ ('marketing_pool.notices.' + block) | translate }}</div>
            }
            <form class="mp__form" (ngSubmit)="createDraft()" novalidate>
              <label class="mp__field">
                <span>{{ 'marketing_pool.new.amount' | translate }}</span>
                <input class="form-control" name="new_amount" inputmode="decimal" autocomplete="off"
                       [placeholder]="o.minimum_amount" [disabled]="!!investBlock() || creating()"
                       [class.is-invalid]="!!newAmountError()" [(ngModel)]="newAmount" (ngModelChange)="onNewInput()" />
                @if (newAmountError(); as key) {
                  <span class="text-danger small">{{ key | translate:{ minimum: o.minimum_amount } }}</span>
                }
              </label>
              @if (!investBlock()) {
                <dl class="mp__dl">
                  <dt>{{ 'marketing_pool.contract.payout_factor' | translate }}</dt>
                  <dd>{{ factor(o.payout_factor) }}@if (o.payout_factor_personal) { <span class="mp__meta ms-1">({{ 'marketing_pool.contract.personal_factor' | translate }})</span> }</dd>
                  <dt>{{ 'marketing_pool.new.preview_total' | translate }}</dt>
                  <dd><b>{{ previewTotal() ? money(previewTotal()) : '—' }}</b></dd>
                </dl>
                <p class="mp__hint">{{ 'marketing_pool.new.included' | translate }}</p>
              }
              @if (offers().length) {
                <p class="mp__hint">{{ 'marketing_pool.new.replaces_draft' | translate }}</p>
              }
              @if (createError(); as message) {
                <div class="alert alert-danger" role="alert">{{ message }}</div>
              }
              <div>
                <button cButton color="primary" type="submit" [disabled]="!canCreate()">
                  {{ (creating() ? 'marketing_pool.new.submitting' : 'marketing_pool.offer.review') | translate }}
                </button>
              </div>
            </form>
          </c-card-body>
        </c-card>
      }

      @if (!investments().length) {
        <c-card class="mp__card">
          <c-card-body>
            <p class="mb-0">{{ 'marketing_pool.investments.empty' | translate }}</p>
            @if (o.visible) { <p class="mp__hint mt-2 mb-0">{{ 'marketing_pool.investments.empty_start' | translate }}</p> }
          </c-card-body>
        </c-card>
      }

      <!-- The partner's own draft, not accepted yet -->
      @for (inv of offers(); track inv.id) {
        <c-card class="mp__card mp__card--accent mp__inv">
          <c-card-body>
            <div class="mp__inv-head">
              <h2 class="mp__h mb-0">{{ 'marketing_pool.offer.title' | translate }}</h2>
              <c-badge [color]="statusColor(inv.status)">{{ ('marketing_pool.status.' + statusKey(inv)) | translate }}</c-badge>
            </div>
            <dl class="mp__dl">
              <dt>{{ 'marketing_pool.contract.investment_id' | translate }}</dt><dd class="mp__ref">{{ inv.reference }}</dd>
              <dt>{{ 'marketing_pool.contract.amount' | translate }}</dt><dd><b>{{ money(inv.amount_usd) }}</b></dd>
              @if (inv.payout_factor) {
                <dt>{{ 'marketing_pool.contract.payout_factor' | translate }}</dt><dd>{{ factor(inv.payout_factor) }}@if (inv.payout_factor_source === 'personal') { <span class="mp__meta ms-1">({{ 'marketing_pool.contract.personal_factor' | translate }})</span> }</dd>
              }
              <dt>{{ 'marketing_pool.contract.total_payout' | translate }}</dt><dd><b>{{ money(inv.total_payout_usd) }}</b></dd>
              <dt>{{ 'marketing_pool.contract.start' | translate }}</dt><dd>{{ 'marketing_pool.offer.start_rule' | translate }}</dd>
              <dt>{{ 'marketing_pool.contract.payout_date' | translate }}</dt><dd>{{ 'marketing_pool.offer.payout_rule' | translate:{ days: o.term_days } }}</dd>
            </dl>
            <p class="mp__hint">{{ 'marketing_pool.offer.included' | translate }}</p>

            @if (acceptBlock() === 'not_open') {
              <div class="alert alert-info" role="note">{{ 'marketing_pool.notices.not_open' | translate }}</div>
            }

            <div class="mp__actions">
              <button cButton color="primary" type="button" [disabled]="!!acceptBlock()" (click)="startAccept(inv)">
                {{ 'marketing_pool.offer.review' | translate }}
              </button>
              <button cButton color="secondary" variant="ghost" type="button" [disabled]="busyId() === inv.id" (click)="decline(inv)">
                {{ 'marketing_pool.offer.decline' | translate }}
              </button>
            </div>
            @if (rowError()?.id === inv.id) { <div class="text-danger small mt-2">{{ rowError()?.message }}</div> }
          </c-card-body>
        </c-card>
      }

      <!-- Accepted participations -->
      @for (inv of contracts(); track inv.id) {
        <c-card class="mp__card mp__inv">
          <c-card-body>
            <div class="mp__inv-head">
              <div>
                <strong class="mp__ref">{{ inv.reference }}</strong>
                <c-badge class="ms-2" [color]="statusColor(inv.status)">{{ ('marketing_pool.status.' + statusKey(inv)) | translate }}</c-badge>
              </div>
              <div class="mp__inv-amount">{{ money(inv.amount_usd) }}</div>
            </div>

            @if ((inv.payout_date || inv.due_at) && (inv.status === 'locked' || inv.status === 'active')) {
              @if (daysLeft(inv.payout_date || inv.due_at); as days) {
                <div class="mp__countdown">
                  @if (days > 0) {
                    {{ 'marketing_pool.investments.countdown' | translate:{ days: days, date: date(inv.payout_date || inv.due_at) } }}
                  } @else {
                    {{ 'marketing_pool.investments.due_reached' | translate }}
                  }
                </div>
              } @else {
                <div class="mp__countdown">{{ 'marketing_pool.investments.due_reached' | translate }}</div>
              }
            }

            <!-- The contract table: the partner's own data only -->
            <dl class="mp__dl">
              <dt>{{ 'marketing_pool.contract.investment_id' | translate }}</dt><dd class="mp__ref">{{ inv.reference }}</dd>
              <dt>{{ 'marketing_pool.contract.amount' | translate }}</dt><dd>{{ money(inv.amount_usd) }}</dd>
              @if (inv.payout_factor) {
                <dt>{{ 'marketing_pool.contract.payout_factor' | translate }}</dt><dd>{{ factor(inv.payout_factor) }}@if (inv.payout_factor_source === 'personal') { <span class="mp__meta ms-1">({{ 'marketing_pool.contract.personal_factor' | translate }})</span> }</dd>
              }
              <dt>{{ 'marketing_pool.contract.total_payout' | translate }}</dt><dd>{{ money(inv.total_payout_usd) }}</dd>
              <dt>{{ 'marketing_pool.contract.start' | translate }}</dt>
              <dd>{{ inv.term_start ? date(inv.term_start) : ('marketing_pool.offer.start_rule' | translate) }}</dd>
              <dt>{{ 'marketing_pool.contract.payout_date' | translate }}</dt>
              <dd>{{ (inv.payout_date || inv.due_at) ? date(inv.payout_date || inv.due_at) : ('marketing_pool.offer.payout_rule' | translate:{ days: o.term_days }) }}</dd>
            </dl>

            <div class="mp__claims">
              <div><span>{{ 'marketing_pool.contract.total_payout' | translate }}</span><b>{{ money(inv.total_payout_usd) }}</b></div>
              <div><span>{{ 'marketing_pool.contract.paid' | translate }}</span><b>{{ money(inv.paid_total ?? 0) }}</b></div>
              <div><span>{{ 'marketing_pool.contract.open' | translate }}</span><b>{{ money(inv.open_total ?? inv.total_payout_usd) }}</b></div>
            </div>

            @if (inv.payouts.length) {
              <h3 class="mp__h3">{{ 'marketing_pool.investments.payouts' | translate }}</h3>
              <ul class="mp__list">
                @for (p of inv.payouts; track p.id) {
                  <li>{{ dateTime(p.approved_at) }} · {{ money(p.amount) }} {{ p.currency }} @if (p.reference) { · {{ p.reference }} }</li>
                }
              </ul>
            }

            <dl class="mp__dl">
              @if (inv.accepted_at) {
                <dt>{{ 'marketing_pool.investments.accepted_at' | translate }}</dt><dd>{{ dateTime(inv.accepted_at) }}</dd>
              }
              @if (inv.payment_method) {
                <dt>{{ 'marketing_pool.investments.payment_method' | translate }}</dt>
                <dd>{{ ('marketing_pool.methods.' + inv.payment_method) | translate }}
                  @if (inv.crypto) { · {{ inv.crypto.token }} ({{ inv.crypto.network }}) }</dd>
              }
              @if (inv.payment_reference) {
                <dt>{{ 'marketing_pool.payment.reference' | translate }}</dt><dd>{{ inv.payment_reference }}</dd>
              }
              @if (inv.crypto?.tx_hash) {
                <dt>{{ 'marketing_pool.investments.tx_hash' | translate }}</dt><dd class="mp__mono">{{ inv.crypto?.tx_hash }}</dd>
              }
              @if (inv.crypto?.amount_received) {
                <dt>{{ 'marketing_pool.investments.amount_received' | translate }}</dt>
                <dd>{{ inv.crypto?.amount_received }} {{ inv.crypto?.token }}</dd>
              }
              @if (inv.paid_at) {
                <dt>{{ 'marketing_pool.investments.paid_at' | translate }}</dt><dd>{{ dateTime(inv.paid_at) }}</dd>
              }
              @if (inv.paid_at || inv.waiver) {
                <dt>{{ 'marketing_pool.investments.withdrawal_ends' | translate }}</dt>
                <dd>{{ inv.waiver ? ('marketing_pool.investments.waived' | translate:{ date: dateTime(inv.waiver.at) }) : dateTime(inv.withdrawal_ends_at) }}</dd>
              }
              @if (inv.contract_version) {
                <dt>{{ 'marketing_pool.investments.contract' | translate }}</dt>
                <dd>{{ inv.contract_version }} ({{ inv.contract_locale }})</dd>
              }
              @if (inv.closed_at) { <dt>{{ 'marketing_pool.investments.closed_at' | translate }}</dt><dd>{{ dateTime(inv.closed_at) }}</dd> }
              @if (inv.withdrawn_at) { <dt>{{ 'marketing_pool.investments.withdrawn_at' | translate }}</dt><dd>{{ dateTime(inv.withdrawn_at) }}</dd> }
              @if (inv.reversed_at) { <dt>{{ 'marketing_pool.investments.reversed_at' | translate }}</dt><dd>{{ dateTime(inv.reversed_at) }}</dd> }
            </dl>

            @if (inv.status === 'pending') {
              <div class="alert alert-info mt-2">
                {{ ('marketing_pool.investments.pending_' + (inv.payment_method || 'bank_transfer')) | translate:{ reference: inv.payment_reference ?? inv.reference } }}
                @if (inv.payment_method !== 'crypto' && bankRows(inv.bank).length) {
                  <dl class="mp__dl mb-0">
                    @for (row of bankRows(inv.bank); track row.key) {
                      @if (row.key !== 'reference') {
                        <dt>{{ ('marketing_pool.payment.bank.' + row.key) | translate }}</dt>
                        <dd>{{ row.value }}</dd>
                      }
                    }
                  </dl>
                }
                @if (inv.payment_method === 'crypto' && inv.order_id) {
                  <div class="mt-2">
                    <button cButton color="primary" size="sm" type="button" (click)="payPending(inv)">{{ 'marketing_pool.investments.pay_now' | translate }}</button>
                  </div>
                }
              </div>
            }

            <div class="mp__actions">
              @for (type of inv.documents; track type) {
                <button cButton size="sm" color="primary" variant="outline" type="button"
                        [disabled]="pdfBusy() === inv.id + ':' + type" (click)="downloadPdf(inv, type)">
                  PDF · {{ ('marketing_pool.pdf.' + type) | translate }}
                </button>
              }
              @if (inv.withdrawable) {
                <button cButton size="sm" color="danger" variant="outline" type="button"
                        [disabled]="busyId() === inv.id" (click)="withdraw(inv)">
                  {{ 'marketing_pool.investments.withdraw' | translate }}
                </button>
              }
            </div>
            @if (inv.withdrawable && inv.withdrawal_ends_at) {
              <p class="mp__hint mt-2 mb-0">{{ 'marketing_pool.investments.withdraw_hint' | translate:{ date: dateTime(inv.withdrawal_ends_at) } }}</p>
            }
            @if (rowError()?.id === inv.id) { <div class="text-danger small mt-2">{{ rowError()?.message }}</div> }
          </c-card-body>
        </c-card>
      }
    }

    <!-- ============================ My referrals ============================ -->
    @if (tab() === 'referrals') {
      <c-card class="mp__card">
        <c-card-body>
          <h2 class="mp__h">{{ 'marketing_pool.referrals.title' | translate }}</h2>
          <p class="mp__hint">{{ 'marketing_pool.referrals.hint' | translate }}</p>
          @if (!referrals().length) {
            <p class="mp__hint mb-0">{{ 'marketing_pool.referrals.empty' | translate }}</p>
          } @else {
            <div class="table-responsive">
              <table class="table table-sm mp__table">
                <thead><tr>
                  <th>{{ 'marketing_pool.referrals.investment' | translate }}</th>
                  <th class="text-end">{{ 'marketing_pool.referrals.amount' | translate }}</th>
                  <th>{{ 'marketing_pool.referrals.dates' | translate }}</th>
                  <th>{{ 'marketing_pool.referrals.commission' | translate }}</th>
                  <th>{{ 'marketing_pool.referrals.claim' | translate }}</th>
                </tr></thead>
                <tbody>
                  @for (r of referrals(); track r.id) {
                    <tr>
                      <td>
                        <div class="mp__ref">{{ r.reference }}</div>
                        <div class="mp__meta">{{ r.investor.name || ('#' + r.investor.id) }}</div>
                        <c-badge [color]="statusColor(r.status)">{{ ('marketing_pool.status.' + r.status) | translate }}</c-badge>
                      </td>
                      <td class="text-end">{{ money(r.amount_usd) }}</td>
                      <td class="mp__meta">
                        <div>{{ 'marketing_pool.investments.paid_at' | translate }}: {{ date(r.paid_at) }}</div>
                        <div>{{ 'marketing_pool.contract.payout_date' | translate }}: {{ date(r.due_at) }}</div>
                      </td>
                      <td>
                        {{ money(r.commission.amount) }}
                        <div class="mp__meta">{{ ('marketing_pool.referrals.commission_status.' + r.commission.status) | translate }}</div>
                      </td>
                      <td>
                        @if (r.claim; as c) {
                          <div>{{ money(c.amount_total) }}</div>
                          <div class="mp__meta">{{ 'marketing_pool.contract.paid' | translate }} {{ money(c.amount_paid) }} ·
                            {{ 'marketing_pool.contract.open' | translate }} {{ money(c.amount_open) }}</div>
                        } @else { — }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </c-card-body>
      </c-card>
    }
    }
  }
</div>
